import React from 'react';
import Icon from '../../../components/AppIcon';

const ProcessTimeline = () => {
  const processSteps = [
    {
      step: 1,
      title: 'Primeiro Contato',
      description: 'Entre em contato conosco via WhatsApp, telefone ou formulário. Responderemos em até 15 minutos.',
      duration: '15 min',
      icon: 'MessageCircle',
      details: [
        'Análise inicial das suas necessidades',
        'Agendamento da consultoria',
        'Envio de material informativo',
        'Preparação da equipe especializada'
      ]
    },
    {
      step: 2,
      title: 'Consultoria Personalizada',
      description: 'Reunião detalhada para entender sua visão, analisar o espaço e apresentar possibilidades.',
      duration: '1-3 horas',
      icon: 'Users',
      details: [
        'Análise do espaço e medições',
        'Discussão de estilo e preferências',
        'Apresentação de materiais e acabamentos',
        'Definição de escopo e cronograma'
      ]
    },
    {
      step: 3,
      title: 'Proposta & Orçamento',
      description: 'Desenvolvimento da proposta técnica com orçamento detalhado e cronograma de execução.',
      duration: '3-5 dias',
      icon: 'FileText',
      details: [
        'Projeto técnico detalhado',
        'Orçamento itemizado',
        'Cronograma de produção',
        'Termos e condições'
      ]
    },
    {
      step: 4,
      title: 'Aprovação & Início',
      description: 'Após aprovação, iniciamos a produção artesanal com acompanhamento constante do progresso.',
      duration: '1-2 dias',
      icon: 'CheckCircle',
      details: [
        'Assinatura do contrato',
        'Pagamento da entrada',
        'Início da produção',
        'Criação do cronograma detalhado'
      ]
    },
    {
      step: 5,
      title: 'Produção Artesanal',
      description: 'Nossos artesãos trabalham na criação das suas peças com atualizações regulares do progresso.',
      duration: '4-12 semanas',
      icon: 'Hammer',
      details: [
        'Seleção e preparação dos materiais',
        'Produção artesanal especializada',
        'Controle de qualidade rigoroso',
        'Atualizações semanais com fotos'
      ]
    },
    {
      step: 6,
      title: 'Entrega & Instalação',
      description: 'Entrega cuidadosa e instalação profissional das suas peças, com garantia completa.',
      duration: '1 dia',
      icon: 'Truck',
      details: [
        'Transporte especializado',
        'Instalação profissional',
        'Verificação final de qualidade',
        'Orientações de cuidado e manutenção'
      ]
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-playfair text-3xl lg:text-4xl font-bold text-primary mb-4">
            Como Funciona Nosso Processo
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Um processo transparente e personalizado, do primeiro contato até a entrega das suas peças únicas
          </p>
        </div>

        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border lg:left-1/2 lg:transform lg:-translate-x-px"></div>

          <div className="space-y-12">
            {processSteps?.map((step, index) => (
              <div
                key={step?.step}
                className={`relative flex items-start space-x-6 lg:space-x-0 ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                }`}
              >
                {/* Timeline Node */}
                <div className="absolute left-8 w-4 h-4 bg-primary rounded-full border-4 border-background lg:left-1/2 lg:transform lg:-translate-x-1/2 z-10"></div>

                {/* Content */}
                <div className={`flex-1 lg:w-1/2 ${index % 2 === 0 ? 'lg:pr-12' : 'lg:pl-12'}`}>
                  <div className="bg-card rounded-2xl p-8 shadow-premium ml-16 lg:ml-0">
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
                        <Icon name={step?.icon} size={24} color="white" />
                      </div>
                      <div>
                        <div className="flex items-center space-x-3 mb-1">
                          <span className="bg-accent text-accent-foreground px-3 py-1 rounded-full text-sm font-semibold">
                            Etapa {step?.step}
                          </span>
                          <span className="text-sm text-text-secondary font-medium">
                            {step?.duration}
                          </span>
                        </div>
                        <h3 className="font-playfair text-xl font-semibold text-primary">
                          {step?.title}
                        </h3>
                      </div>
                    </div>

                    <p className="text-text-secondary mb-6 leading-relaxed">
                      {step?.description}
                    </p>

                    <div className="space-y-3">
                      {step?.details?.map((detail, detailIndex) => (
                        <div key={detailIndex} className="flex items-center space-x-3">
                          <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                          <span className="text-sm text-text-primary">{detail}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Spacer for alternating layout */}
                <div className="hidden lg:block lg:w-1/2"></div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-forest rounded-2xl p-8 lg:p-12 text-white">
            <h3 className="font-playfair text-2xl lg:text-3xl font-bold mb-4">
              Pronto para Começar Seu Projeto?
            </h3>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Nossa equipe está pronta para transformar suas ideias em móveis únicos e exclusivos
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-accent text-accent-foreground px-8 py-4 rounded-xl font-semibold hover:bg-accent/90 transition-colors duration-300 flex items-center justify-center space-x-2">
                <Icon name="MessageCircle" size={20} />
                <span>Iniciar pelo WhatsApp</span>
              </button>
              <button className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-primary transition-colors duration-300 flex items-center justify-center space-x-2">
                <Icon name="Calendar" size={20} />
                <span>Agendar Consultoria</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessTimeline;